import React, { useState } from 'react';
import { supabase } from '../supabase';

interface OnlineClassFormProps {
  teacherId: string;
  teacherName: string;
  subject: string;
  onClassCreated: () => void;
}

export default function OnlineClassForm({
  teacherId,
  teacherName,
  subject,
  onClassCreated,
}: OnlineClassFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('60');
  const [maxStudents, setMaxStudents] = useState('30');
  const [classLink, setClassLink] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!title || !date || !time) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      const { error: insertErr } = await supabase.from('online_classes').insert({
        teacher_id: teacherId,
        teacher_name: teacherName,
        subject: subject,
        title: title,
        description: description,
        schedule: `${date}T${time}`,
        meeting_link: classLink,
      });
      if (insertErr) throw insertErr;

      setSuccess('Class created successfully!');
      setTitle('');
      setDescription('');
      setDate('');
      setTime('');
      setDuration('60');
      setMaxStudents('30');
      setClassLink('');

      setTimeout(() => {
        setIsOpen(false);
        onClassCreated();
      }, 1500);
    } catch (error: any) {
      setError('Failed to create class: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full bg-gradient-to-r from-primary to-indigo-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
      >
        <span className="material-symbols-outlined">video_call</span>
        Create Online Class
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-5 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start sm:items-center justify-between mb-6 gap-4">
          <h2 className="text-xl sm:text-2xl font-extrabold text-navy">Create Online Class</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {success}
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-navy mb-2">
              Class Title *
            </label>
            <input
              type="text"
              id="online-class-title"
              name="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Advanced Mathematics - Chapter 5"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-navy mb-2">
              Description
            </label>
            <textarea
              id="online-class-description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the class content and what students will learn"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary h-20 resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-navy mb-2">
                Date *
              </label>
              <input
                type="date"
                id="online-class-date"
                name="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-navy mb-2">
                Time *
              </label>
              <input
                type="time"
                id="online-class-time"
                name="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-navy mb-2">
                Duration (minutes)
              </label>
              <input
                type="number"
                id="online-class-duration"
                name="duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="60"
                min="15"
                max="180"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-navy mb-2">
                Max Students
              </label>
              <input
                type="number"
                id="online-class-max-students"
                name="maxStudents"
                value={maxStudents}
                onChange={(e) => setMaxStudents(e.target.value)}
                placeholder="30"
                min="1"
                max="500"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-navy mb-2">
              Class Link (Google Meet, Zoom, etc.)
            </label>
            <input
              type="url"
              id="online-class-link"
              name="classLink"
              value={classLink}
              onChange={(e) => setClassLink(e.target.value)}
              placeholder="https://meet.google.com/..."
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Class'}
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 border border-slate-300 text-slate-700 py-3 rounded-lg font-bold hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
